import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Sun, Moon, Zap, Cpu } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/Authcontext';
import { useTheme } from '../../contexts/Themecontext';
import logoImg from '../../assets/img/logo.png';

const QuantumNavbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const { isAuthenticated, isAdmin } = useAuth();
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  const quantumMenu = [
    { label: 'Quantum Hub', path: '/', submenu: null },
    { label: 'Origin Story', path: '/about', submenu: null },
    {
      label: 'Quantum Products',
      path: '#',
      submenu: [
        { label: 'CoreStack Processors', path: '/products' },
        { label: 'QuantumEdge Platform', path: '/solutions' },
        { label: 'QubitWorks Suite', path: '/software' },
      ],
    },
    {
      label: 'Technologies',
      path: '#',
      submenu: [
        { label: 'NanoCore Semiconductor', path: '/semiconductor' },
        { label: 'SuperFlux Superconductor', path: '/superconductor' },
        { label: 'Quantum AI Nexus', path: '/ai' },
        { label: 'HyperCore Hardware', path: '/hardware' },
      ],
    },
    { label: 'Research Lab', path: '/research', submenu: null },
    {
      label: 'Innovation',
      path: '#',
      submenu: [
        { label: 'Breakthrough Hub', path: '/innovation' },
        { label: 'Future Roadmap', path: '/roadmap' },
        { label: 'Join Our Team', path: '/careers' },
      ],
    },
    { label: 'Connect', path: '/contact', submenu: null },
  ];

  const getThemeIcon = () => {
    if (theme === 'light') return <Sun className="w-4 h-4 sm:w-5 sm:h-5" />;
    if (theme === 'dark') return <Moon className="w-4 h-4 sm:w-5 sm:h-5" />;
    return <Cpu className="w-4 h-4 sm:w-5 sm:h-5" />;
  };

  const getThemeStyles = () => {
    if (theme === 'light') {
      return 'bg-white/95 backdrop-blur-xl border-gray-200 text-gray-900';
    }
    if (theme === 'dark') {
      return 'bg-slate-900/95 backdrop-blur-xl border-slate-700 text-white';
    }
    // Quantum theme with semiconductor gradient
    return 'bg-gradient-to-r from-slate-950/95 via-indigo-950/95 to-purple-950/95 backdrop-blur-xl border-purple-500/30 text-white';
  };

  return (
    <>
      {/* Navbar */}
      <nav
        className={`fixed top-0 left-0 w-full z-[9999] transition-all duration-300 border-b ${getThemeStyles()} ${
          isScrolled ? 'shadow-2xl shadow-purple-500/10' : ''
        }`}
      >
        <div className="max-w-[1920px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16 md:h-18 lg:h-20">
            {/* Logo & Brand */}
            <Link to="/" className="flex items-center gap-2 sm:gap-3 group z-50">
              <motion.img
                src={logoImg}
                alt="GLYQIX Logo"
                className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 object-contain"
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.6 }}
              />
              <div className="flex items-center gap-0.5 sm:gap-1">
                <span
                  className={`text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold tracking-tight ${
                    theme === 'quantum'
                      ? 'bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent'
                      : theme === 'dark'
                      ? 'text-cyan-400'
                      : 'text-blue-600'
                  }`}
                >
                  GLY
                </span>
                <span
                  className={`text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold tracking-tight ${
                    theme === 'quantum'
                      ? 'bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent'
                      : theme === 'dark'
                      ? 'text-purple-400'
                      : 'text-purple-600'
                  }`}
                >
                  QIX
                </span>
              </div>
            </Link>

            {/* Desktop Navigation - Hidden on mobile/tablet */}
            <div className="hidden xl:flex items-center gap-1 2xl:gap-2">
              {quantumMenu.map((item, idx) => (
                <div
                  key={idx}
                  className="relative"
                  onMouseEnter={() => item.submenu && setActiveDropdown(item.label)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  {item.submenu ? (
                    <div className="relative">
                      <button
                        className={`flex items-center gap-1 px-3 py-2 text-sm 2xl:text-base font-medium rounded-lg transition-all duration-200 ${
                          theme === 'quantum'
                            ? 'text-gray-200 hover:bg-purple-500/20 hover:text-purple-300'
                            : theme === 'dark'
                            ? 'text-gray-300 hover:bg-slate-800 hover:text-white'
                            : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                        }`}
                      >
                        {item.label}
                        <ChevronDown
                          className={`w-4 h-4 transition-transform duration-200 ${
                            activeDropdown === item.label ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      <AnimatePresence>
                        {activeDropdown === item.label && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                            className={`absolute top-full left-0 mt-2 w-56 rounded-xl shadow-2xl border overflow-hidden ${
                              theme === 'quantum'
                                ? 'bg-slate-900/95 border-purple-500/30 backdrop-blur-xl'
                                : theme === 'dark'
                                ? 'bg-slate-800/95 border-slate-700 backdrop-blur-xl'
                                : 'bg-white/95 border-gray-200 backdrop-blur-xl'
                            }`}
                          >
                            {item.submenu.map((sub, sIdx) => (
                              <Link
                                key={sIdx}
                                to={sub.path}
                                className={`flex items-center gap-3 px-4 py-3 text-sm transition-all duration-200 ${
                                  theme === 'quantum'
                                    ? 'text-gray-200 hover:bg-purple-500/20 hover:text-purple-300'
                                    : theme === 'dark'
                                    ? 'text-gray-300 hover:bg-slate-700 hover:text-white'
                                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                                }`}
                              >
                                <span
                                  className={`w-1.5 h-1.5 rounded-full ${
                                    theme === 'quantum'
                                      ? 'bg-gradient-to-r from-cyan-400 to-purple-500'
                                      : theme === 'dark'
                                      ? 'bg-cyan-400'
                                      : 'bg-blue-500'
                                  }`}
                                />
                                {sub.label}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link
                      to={item.path}
                      className={`px-3 py-2 text-sm 2xl:text-base font-medium rounded-lg transition-all duration-200 ${
                        location.pathname === item.path
                          ? theme === 'quantum'
                            ? 'bg-purple-500/20 text-purple-300'
                            : theme === 'dark'
                            ? 'bg-slate-800 text-white'
                            : 'bg-gray-100 text-gray-900'
                          : theme === 'quantum'
                          ? 'text-gray-200 hover:bg-purple-500/20 hover:text-purple-300'
                          : theme === 'dark'
                          ? 'text-gray-300 hover:bg-slate-800 hover:text-white'
                          : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
              {/* Theme Toggle */}
              <motion.button
                onClick={toggleTheme}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`p-2 rounded-lg transition-all duration-200 ${
                  theme === 'quantum'
                    ? 'bg-purple-500/20 text-purple-300 hover:bg-purple-500/30'
                    : theme === 'dark'
                    ? 'bg-slate-800 text-gray-300 hover:bg-slate-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                title={`Switch theme (${theme})`}
              >
                {getThemeIcon()}
              </motion.button>

              {/* Auth Buttons - Desktop */}
              {!isAuthenticated ? (
                <>
                  <Link
                    to="/login"
                    className={`hidden sm:block px-3 md:px-4 py-2 text-sm md:text-base font-medium rounded-lg transition-all duration-200 ${
                      theme === 'quantum'
                        ? 'text-gray-200 hover:text-purple-300'
                        : theme === 'dark'
                        ? 'text-gray-300 hover:text-white'
                        : 'text-gray-700 hover:text-gray-900'
                    }`}
                  >
                    Login
                  </Link>
                  <Link
                    to="/get-started"
                    className={`px-3 sm:px-4 md:px-5 lg:px-6 py-2 md:py-2.5 text-xs sm:text-sm md:text-base font-semibold rounded-lg transition-all duration-200 shadow-lg whitespace-nowrap ${
                      theme === 'quantum'
                        ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-500 hover:to-pink-500 shadow-purple-500/50'
                        : theme === 'dark'
                        ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white hover:from-cyan-500 hover:to-blue-500 shadow-cyan-500/50'
                        : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-500 hover:to-purple-500 shadow-blue-500/50'
                    }`}
                  >
                    Get Started
                  </Link>
                </>
              ) : (
                <Link
                  to={isAdmin ? '/admin' : '/dashboard'}
                  className={`px-3 sm:px-4 md:px-5 lg:px-6 py-2 md:py-2.5 text-xs sm:text-sm md:text-base font-semibold rounded-lg transition-all duration-200 shadow-lg whitespace-nowrap ${
                    theme === 'quantum'
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-500 hover:to-pink-500 shadow-purple-500/50'
                      : theme === 'dark'
                      ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white hover:from-cyan-500 hover:to-blue-500 shadow-cyan-500/50'
                      : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-500 hover:to-purple-500 shadow-blue-500/50'
                  }`}
                >
                  {isAdmin ? 'Admin Portal' : 'Dashboard'}
                </Link>
              )}

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`xl:hidden p-2 rounded-lg transition-all duration-200 ${
                  theme === 'quantum'
                    ? 'text-gray-200 hover:bg-purple-500/20'
                    : theme === 'dark'
                    ? 'text-gray-300 hover:bg-slate-800'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Quantum Animation Line */}
        {theme === 'quantum' && (
          <div className="absolute bottom-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-purple-500 to-transparent opacity-50">
            <motion.div
              className="h-full w-1/3 bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500"
              animate={{
                x: ['-100%', '300%'],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'linear',
              }}
            />
          </div>
        )}
      </nav>

      {/* Mobile Menu Overlay & Sidebar */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998] xl:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            />

            {/* Sidebar */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className={`fixed top-0 right-0 bottom-0 w-[85vw] max-w-sm z-[9999] overflow-y-auto ${
                theme === 'quantum'
                  ? 'bg-gradient-to-b from-slate-950 via-indigo-950 to-purple-950'
                  : theme === 'dark'
                  ? 'bg-slate-900'
                  : 'bg-white'
              } shadow-2xl xl:hidden`}
            >
              {/* Mobile Header */}
              <div
                className={`sticky top-0 z-10 flex items-center justify-between p-4 sm:p-5 border-b ${
                  theme === 'quantum'
                    ? 'bg-slate-950/80 backdrop-blur-xl border-purple-500/30'
                    : theme === 'dark'
                    ? 'bg-slate-900/80 backdrop-blur-xl border-slate-700'
                    : 'bg-white/80 backdrop-blur-xl border-gray-200'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`text-xl sm:text-2xl font-bold ${
                      theme === 'quantum'
                        ? 'bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent'
                        : theme === 'dark'
                        ? 'text-cyan-400'
                        : 'text-blue-600'
                    }`}
                  >
                    GLY
                  </span>
                  <span
                    className={`text-xl sm:text-2xl font-bold ${
                      theme === 'quantum'
                        ? 'bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent'
                        : theme === 'dark'
                        ? 'text-purple-400'
                        : 'text-purple-600'
                    }`}
                  >
                    QIX
                  </span>
                </div>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`p-2 rounded-lg transition-all duration-200 ${
                    theme === 'quantum'
                      ? 'text-gray-200 hover:bg-purple-500/20'
                      : theme === 'dark'
                      ? 'text-gray-300 hover:bg-slate-800'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Mobile Content */}
              <div className="p-4 sm:p-5 space-y-2">
                {/* Theme Toggle Mobile */}
                <button
                  onClick={toggleTheme}
                  className={`w-full flex items-center justify-between p-3 sm:p-4 rounded-xl transition-all duration-200 ${
                    theme === 'quantum'
                      ? 'bg-purple-500/10 text-purple-300 hover:bg-purple-500/20'
                      : theme === 'dark'
                      ? 'bg-slate-800 text-gray-300 hover:bg-slate-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span className="flex items-center gap-3 text-sm sm:text-base font-medium">
                    {getThemeIcon()}
                    <span>Theme: {theme.charAt(0).toUpperCase() + theme.slice(1)}</span>
                  </span>
                </button>

                {/* Mobile Navigation Links */}
                {quantumMenu.map((item, idx) => (
                  <div key={idx}>
                    {item.submenu ? (
                      <div
                        className={`rounded-xl overflow-hidden ${
                          theme === 'quantum'
                            ? 'bg-purple-500/5'
                            : theme === 'dark'
                            ? 'bg-slate-800/50'
                            : 'bg-gray-50'
                        }`}
                      >
                        <p
                          className={`px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-semibold tracking-wider uppercase ${
                            theme === 'quantum'
                              ? 'text-purple-300'
                              : theme === 'dark'
                              ? 'text-cyan-400'
                              : 'text-blue-600'
                          }`}
                        >
                          {item.label}
                        </p>
                        <div className="space-y-1 p-2">
                          {item.submenu.map((sub, sIdx) => (
                            <Link
                              key={sIdx}
                              to={sub.path}
                              className={`flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg text-sm sm:text-base transition-all duration-200 ${
                                theme === 'quantum'
                                  ? 'text-gray-200 hover:bg-purple-500/20 hover:text-purple-300'
                                  : theme === 'dark'
                                  ? 'text-gray-300 hover:bg-slate-700 hover:text-white'
                                  : 'text-gray-700 hover:bg-white hover:text-gray-900'
                              }`}
                            >
                              <span
                                className={`w-2 h-2 rounded-full ${
                                  theme === 'quantum'
                                    ? 'bg-gradient-to-r from-cyan-400 to-purple-500'
                                    : theme === 'dark'
                                    ? 'bg-cyan-400'
                                    : 'bg-blue-500'
                                }`}
                              />
                              {sub.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <Link
                        to={item.path}
                        className={`block px-3 sm:px-4 py-3 sm:py-4 rounded-xl text-sm sm:text-base font-medium transition-all duration-200 ${
                          location.pathname === item.path
                            ? theme === 'quantum'
                              ? 'bg-purple-500/20 text-purple-300'
                              : theme === 'dark'
                              ? 'bg-slate-800 text-white'
                              : 'bg-gray-100 text-gray-900'
                            : theme === 'quantum'
                            ? 'text-gray-200 hover:bg-purple-500/10'
                            : theme === 'dark'
                            ? 'text-gray-300 hover:bg-slate-800'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {item.label}
                      </Link>
                    )}
                  </div>
                ))}

                {/* Mobile Auth Buttons */}
                <div className={`pt-4 space-y-3 border-t ${
                  theme === 'quantum'
                    ? 'border-purple-500/30'
                    : theme === 'dark'
                    ? 'border-slate-700'
                    : 'border-gray-200'
                }`}>
                  {!isAuthenticated ? (
                    <>
                      <Link
                        to="/login"
                        className={`block w-full px-4 py-3 text-center text-base font-semibold rounded-xl transition-all duration-200 ${
                          theme === 'quantum'
                            ? 'bg-purple-500/10 text-purple-300 hover:bg-purple-500/20'
                            : theme === 'dark'
                            ? 'bg-slate-800 text-gray-300 hover:bg-slate-700'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        Login
                      </Link>
                      <Link
                        to="/signup"
                        className={`block w-full px-4 py-3 text-center text-base font-semibold rounded-xl transition-all duration-200 shadow-lg ${
                          theme === 'quantum'
                            ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-500 hover:to-pink-500 shadow-purple-500/50'
                            : theme === 'dark'
                            ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white hover:from-cyan-500 hover:to-blue-500 shadow-cyan-500/50'
                            : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-500 hover:to-purple-500 shadow-blue-500/50'
                        }`}
                      >
                        Get Started
                      </Link>
                    </>
                  ) : (
                    <Link
                      to={isAdmin ? '/admin' : '/dashboard'}
                      className={`block w-full px-4 py-3 text-center text-base font-semibold rounded-xl transition-all duration-200 shadow-lg ${
                        theme === 'quantum'
                          ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-500 hover:to-pink-500 shadow-purple-500/50'
                          : theme === 'dark'
                          ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white hover:from-cyan-500 hover:to-blue-500 shadow-cyan-500/50'
                          : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-500 hover:to-purple-500 shadow-blue-500/50'
                      }`}
                    >
                      {isAdmin ? 'Admin Portal' : 'Dashboard'}
                    </Link>
                  )}
                </div>
              </div>

              {/* Quantum Particles Animation for Mobile Menu */}
              {theme === 'quantum' && (
                <div className="absolute inset-0 pointer-events-none overflow-hidden">
                  {[...Array(15)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="absolute w-1 h-1 bg-purple-500/30 rounded-full"
                      style={{
                        left: `${Math.random() * 100}%`,
                        top: `${Math.random() * 100}%`,
                      }}
                      animate={{
                        y: [0, -20, 0],
                        opacity: [0.2, 0.8, 0.2],
                      }}
                      transition={{
                        duration: 2 + Math.random() * 2,
                        repeat: Infinity,
                        delay: Math.random() * 2,
                      }}
                    />
                  ))}
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default QuantumNavbar;